import PropTypes from 'prop-types'
import { faAngleDown } from '@fortawesome/free-solid-svg-icons'
import { CueStartMode } from '@rundown-studio/types'
import formatTimeOfDay from '../../../utils/formatTimeOfDay.js'
import { formatCalendarDate } from '@rundown-studio/timeutils'
import { useMemo, lazy, Suspense } from 'react'

const Tooltip = lazy(() =>
  import('../../interactives/Tooltip.jsx').then((m) => ({
    default: m.Tooltip,
  })),
)
const TooltipContent = lazy(() =>
  import('../../interactives/Tooltip.jsx').then((m) => ({
    default: m.TooltipContent,
  })),
)
const TooltipTrigger = lazy(() =>
  import('../../interactives/Tooltip.jsx').then((m) => ({
    default: m.TooltipTrigger,
  })),
)
const FontAwesomeIcon = lazy(() =>
  import('@fortawesome/react-fontawesome').then((m) => ({
    default: m.FontAwesomeIcon,
  })),
)

/**
 * StartTimeSupDisplay Component
 *
 * sup = above content
 *
 * Displays a toggle button for controlling cue scheduling (auto-advance or fixed time)
 * in the top section of a cue timing cell.
 */
export default function StartTimeSupDisplay({
  startTime,
  startMode = CueStartMode.FLEXIBLE,
  scheduled,
  setScheduled,
  readonly = false,
  timezone,
  todDisplayFormat,
}) {
  const isTimeInPast =
    startMode === CueStartMode.FIXED && startTime && startTime < new Date()
  const isButtonDisabled = readonly || (isTimeInPast && !scheduled)
  const isPulsating =
    startMode === CueStartMode.FIXED &&
    scheduled &&
    startTime &&
    startTime > new Date()

  const tooltipText = useMemo(() => {
    if (startMode === CueStartMode.FLEXIBLE) {
      if (scheduled && readonly) return 'Cue auto-starts after previous cue'
      return scheduled
        ? 'Disable auto-start after previous cue'
        : 'Enable auto-start after previous cue'
    } else {
      // Fixed mode
      if (isTimeInPast && !scheduled) {
        return 'Cannot enable auto-start, time is in the past. Adjust rundown date or cue time.'
      }
      if (!startTime) return scheduled ? 'Auto-start enabled' : 'Enable auto-start'
      const dateStr = formatCalendarDate(startTime, {
        timezone,
        format: 'MMM d',
      })
      const timeStr = formatTimeOfDay(startTime, {
        timezone,
        seconds: 'nonzero',
        format: todDisplayFormat,
      })
      const timeStatus = isTimeInPast ? '(past)' : '(future)'
      if (scheduled && readonly)
        return `Auto-starts at ${timeStr} on ${dateStr} ${timeStatus}`
      return scheduled
        ? `Disable auto-start at ${timeStr} on ${dateStr} ${timeStatus}`
        : `Enable auto-start at ${timeStr} on ${dateStr} ${timeStatus}`
    }
  }, [
    isTimeInPast,
    readonly,
    scheduled,
    startMode,
    startTime,
    timezone,
    todDisplayFormat,
  ])

  if (readonly && !scheduled) return null

  return (
    <Suspense>
      <Tooltip>
        <TooltipTrigger>
          <button
            className={[
              'relative block w-full h-5 text-center text-xs leading-0 rounded-t transition-colors bg-white/0 text-white',
              isButtonDisabled ? 'cursor-default!' : 'hover:bg-white/10',
            ].join(' ')}
            onClick={() => setScheduled(!scheduled)}
            disabled={isButtonDisabled}
          >
            <div
              className={[
                scheduled ? 'opacity-100' : 'opacity-0 group-hover:opacity-40',
                isPulsating ? 'animate-pulse' : '',
              ].join(' ')}
            >
              {startMode === CueStartMode.FLEXIBLE ? (
                <>
                  <div className="absolute -top-1 left-1/2 -ml-[0.5px] w-px h-[15px] rounded bg-current" />
                  <FontAwesomeIcon icon={faAngleDown} size="xs" />
                </>
              ) : (
                <>
                  <div className="absolute top-[3px] left-1/2 -ml-[6px] w-[12px] h-px rounded bg-current" />
                  <div className="absolute top-[3px] left-1/2 -ml-[0.5px] w-px h-[8px] rounded bg-current" />
                  <FontAwesomeIcon icon={faAngleDown} size="xs" />
                </>
              )}
            </div>
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <span className="text-xs">{tooltipText}</span>
        </TooltipContent>
      </Tooltip>
    </Suspense>
  )
}

StartTimeSupDisplay.propTypes = {
  startTime: PropTypes.instanceOf(Date),
  startMode: PropTypes.oneOf(Object.values(CueStartMode)),
  scheduled: PropTypes.bool,
  setScheduled: PropTypes.func.isRequired,
  readonly: PropTypes.bool,
  timezone: PropTypes.string,
  todDisplayFormat: PropTypes.string,
}
